import { TooltipIcon } from '@ipfabric/ui-components/Tooltip/TooltipIcon';
import {
  DEBOUNCE_TEXT_INPUT_CHANGES_DEFAULT,
  useDebounce,
} from '@ipfabric/ui-components/hooks/useDebounce';
import { withTargetValue } from '@ipfabric/ui-components/utils/events/withTargetValue';
import type { Awaitable } from '@ipfabric/ui-components/utils/types/Awaitable';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { ScrollPaginator } from 'components/ScrollPaginator/ScrollPaginator';
import { TextInput } from 'components/TextInput/TextInput';
import { useLocale } from 'containers/App/LocaleContext';
import { useRef, useState } from 'react';
import { type FC, Fragment, type RefObject } from 'react';
import type Scrollbars from 'react-custom-scrollbars-2';
import { useOverflowPosition } from 'utils/hooks/useOverflowPosition';
import { useArrowKeyNavigation } from 'utils/keyboard/useArrowKeyNavigation';
import {
  type InfiniteScrollItemsResult,
  useInfiniteScrollItems,
} from 'utils/react/useInfiniteScrollItems';

export type FacetedFilterAttributeOption = {
  value: string;
  /**
   * Option is still available but for some reason grayed out.
   *
   * When other conditions make given option unavailable (e.g. no match in conbination
   * with other filters or current discovery snapshot), it is visually indicated
   * but the option is still available for some scenarios. For example, it shouldn't
   * be automatically unchecked as the user doesn't want to lose the filter when
   * reaching a state where given value is not available, and it must be possible
   * to uncheck it still.
   */
  grayedOutReason?: string;
};

interface Props {
  loadAttributes: (
    startIndex: number,
    filter: string,
  ) => Awaitable<InfiniteScrollItemsResult<FacetedFilterAttributeOption>>;
  selectOption: (option: string) => void;
  wrapperRef: RefObject<HTMLElement>;
  selected: string[];
}

export const FacetedFilterNewAttributeSelect: FC<Props> = (props) => {
  const {
    generic: {
      components: { facetedFilter: facetedFilterMsg },
    },
  } = useLocale();
  const ctxProps = useGlobalStateContextSelectors({
    contentWrapper: prop('contentWrapper'),
  });
  // Actual value of input and filter is separated to allow us to debounce
  const [filterValue, setFilterValue] = useState<string>('');
  const [filter, setFilter] = useState<string>('');
  const { items, scrollPaginatorProps } = useInfiniteScrollItems({
    loadItems: (startIndex: number) => props.loadAttributes(startIndex, filter),
    hoistedItems: [],
    reloadDeps: [filter],
    getItemKey: (k) => k.value,
  });
  const scrollViewRef = useRef<Scrollbars>(null);
  const { handleOptionMouseEnter } = useArrowKeyNavigation({
    scrollViewRef,
    wrapperRef: props.wrapperRef,
  });
  const debounce = useDebounce(DEBOUNCE_TEXT_INPUT_CHANGES_DEFAULT);

  const handleSearchChange = (value: string) => {
    setFilterValue(value);

    debounce(() => {
      setFilter(value);
    });
  };

  const getGroup = (option: string): string | null => {
    const labels = option.split('.');

    // Fixed noUncheckedIndexedAccess NIM-19408
    return labels.length > 1 ? labels[0]! : null;
  };

  const getLabel = (option: string): string =>
    option.substring(option.indexOf('.') + 1);

  const elementRef = useRef<HTMLDivElement>(null);
  const relativeElement = ctxProps.contentWrapper;
  const style = useOverflowPosition(elementRef, relativeElement);

  return (
    <div className='faceted-filter__dropdown' ref={elementRef} style={style}>
      <div className='mx-2.5 mb-2 mt-1 text-xl'>
        {facetedFilterMsg.selectAttribute}
      </div>
      <TextInput
        className='m-2.5'
        autoFocus
        type='search'
        placeholder={facetedFilterMsg.search}
        value={filterValue}
        dataTestId='attribute-search'
        onChange={withTargetValue(handleSearchChange)}
      />
      <div className='faceted-filter__options'>
        <ScrollPaginator
          {...scrollPaginatorProps}
          scrollViewRef={scrollViewRef}
        >
          {items.map((option, i) => {
            const group = getGroup(option.value);
            // Fixed noUncheckedIndexedAccess NIM-19408
            const previousGroup = i > 0 ? getGroup(items[i - 1]!.value) : null;
            const showGroup = group !== null && group !== previousGroup;

            return (
              <Fragment key={option.value}>
                {showGroup && (
                  <div className='faceted-filter__attribute-group'>{group}</div>
                )}
                <button
                  type='button'
                  className={classNames(
                    'faceted-filter__attribute-option',
                    option.grayedOutReason && 'text-disabled',
                    group !== null &&
                      'faceted-filter__attribute-option--grouped',
                  )}
                  onClick={() => {
                    props.selectOption(option.value);
                  }}
                  tabIndex={-1}
                  onMouseEnter={handleOptionMouseEnter}
                  disabled={props.selected.includes(option.value)}
                >
                  {getLabel(option.value)}
                  {option.grayedOutReason && (
                    <TooltipIcon content={option.grayedOutReason} />
                  )}
                </button>
              </Fragment>
            );
          })}
          {scrollPaginatorProps.total === 0 && (
            <p className='pl-4 pt-2'>
              {!filterValue
                ? facetedFilterMsg.noAttributeFoundSnapshot
                : facetedFilterMsg.noAttributeFoundFilter}
            </p>
          )}
        </ScrollPaginator>
      </div>
    </div>
  );
};
